/**
 * Pure helpers for the mapping step's value-map panel
 * (quantix-api/docs/campaigns/field-mapping.md §4.3): deciding which targets
 * need a value map, collecting the known source values/options to map from,
 * and auto-filling the panel by case-insensitive label match (e.g.
 * "MetaTrader 5" -> `MT5`).
 */

import type { SourceField, SourceFieldOption } from '@/api/campaigns/types';
import type { AvailableFieldConfig } from '@/components/leads/mapping/mappingValidation';

const ENUM_LIKE_TYPES = [
    'enum',
    'multi-enum',
    'multi_enum',
    'select',
    'multiselect',
    'multi-select',
];

export interface TargetOption {
    key: string;
    label: string;
}

/** Whether a catalog target field is an enum/select/multiselect kind (field-mapping.md §4.3). */
export function isEnumLikeTarget(fieldConfig: AvailableFieldConfig | undefined | null): boolean {
    if (!fieldConfig) return false;
    const type = fieldConfig.type ? String(fieldConfig.type).toLowerCase() : '';
    if (ENUM_LIKE_TYPES.includes(type)) return true;
    return Array.isArray(fieldConfig.options) && fieldConfig.options.length > 0;
}

/** Normalizes a target field's `options` (string[] or {key,label}[]) into `{key,label}[]`. */
export function targetOptionList(
    fieldConfig: AvailableFieldConfig | undefined | null
): TargetOption[] {
    if (!fieldConfig?.options) return [];
    return fieldConfig.options.map(option =>
        typeof option === 'string'
            ? { key: option, label: option }
            : { key: option.key, label: option.label }
    );
}

/**
 * Union of known values for the given source field keys: each source's
 * declared `options` (Facebook multiple-choice questions), falling back to
 * its captured/declared `sample` as a single value when there are no
 * `options` (field-mapping.md §4.3: "the user lists expected values, or they
 * are taken from the captured sample"). De-duplicated by option key.
 */
export function collectSourceValueOptions(
    sourceFields: SourceField[],
    sourceKeys: string[]
): SourceFieldOption[] {
    const seen = new Set<string>();
    const result: SourceFieldOption[] = [];

    sourceKeys.forEach(key => {
        const field = sourceFields.find(f => f.key === key);
        if (!field) return;

        if (field.options && field.options.length > 0) {
            field.options.forEach(option => {
                if (!seen.has(option.key)) {
                    seen.add(option.key);
                    result.push(option);
                }
            });
            return;
        }

        if (field.sample !== undefined && field.sample !== null && field.sample !== '') {
            const sampleValue = String(field.sample);
            if (!seen.has(sampleValue)) {
                seen.add(sampleValue);
                result.push({ key: sampleValue, label: sampleValue });
            }
        }
    });

    return result;
}

/**
 * Auto-fills a value map: each source option -> the target option whose key
 * or label matches case-insensitively, trying the source's label first and
 * its key second. Source options with no match are left out of the result
 * (shown as "unmatched" by the panel, not an empty-string mapping).
 */
export function autoFillValueMap(
    sourceOptions: SourceFieldOption[],
    targetOptions: TargetOption[]
): Record<string, string> {
    const normalizedTargets = targetOptions.map(option => ({
        key: option.key,
        label: option.label,
        keyLower: option.key.trim().toLowerCase(),
        labelLower: option.label.trim().toLowerCase(),
    }));

    const result: Record<string, string> = {};

    sourceOptions.forEach(sourceOption => {
        const labelLower = sourceOption.label.trim().toLowerCase();
        const keyLower = sourceOption.key.trim().toLowerCase();

        const match =
            normalizedTargets.find(t => t.labelLower === labelLower || t.keyLower === labelLower) ??
            normalizedTargets.find(t => t.labelLower === keyLower || t.keyLower === keyLower);

        if (match) {
            result[sourceOption.key] = match.key;
        }
    });

    return result;
}
